import { useDirection } from "@radix-ui/react-direction";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight, Circle } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const DropdownMenu = ({
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) => {
  const dir = useDirection();

  return (
    <DropdownMenuPrimitive.Root dir={dir} {...props}>
      {children}
    </DropdownMenuPrimitive.Root>
  );
};

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      // NOTE: NSAI-6259 follow-up — the same row scale as `DropdownMenuItem`,
      // so a flyout row (the theme picker, move-to-folder) sits at the same
      // height as the plain rows above and below it.
      "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-start text-sm outline-none focus:bg-accent data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
      "min-h-8 pointer-coarse:min-h-11",
      className,
    )}
    {...props}
  >
    {children}
    <ChevronRight className="ms-auto h-4 w-4 rtl:rotate-180" />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

// NOTE: NSAI-5708 — a submenu must not close because the PARENT MENU CONTAINER
// took focus. Radix steals that focus itself, from `onItemLeave`:
//
//   onItemLeave: (event) => {
//     if (isPointerMovingToSubmenu(event)) return;
//     contentRef.current?.focus();   // <- the parent menu container
//   }
//
// and `MenuSubContent` reads any focus move out of itself as a dismissal
// (exempting only the sub-trigger), so that steal closes the open submenu.
//
// `isPointerMovingToSubmenu` is where the RTL asymmetry lives. It compares the
// last recorded horizontal pointer direction against the side the submenu sits
// on — and that direction is a ref initialised to "right" that is only updated
// while the pointer is over the parent content AND its clientX changes:
//
//   const pointerDirRef = React.useRef("right");
//   ...
//   if (event.currentTarget.contains(target) && pointerXHasChanged) { ... }
//
// Move straight down out of a sub-trigger and then across into the submenu —
// what a hand does when the target sits low in a tall panel — and the pointer
// leaves the parent content before any horizontal movement is recorded, so the
// direction stays at its "right" default. In LTR the submenu IS on the right,
// the stale default happens to match, and the guard holds. In RTL the submenu
// is on the left, "right" never matches "left", and the panel closes every
// time. QA hit this on the Token Usage date-range filter in Arabic: the
// calendar is tall enough that most days are below the filter menu, so the
// filter could not be applied at all.
//
// The exemption below is the same kind Radix already makes for the sub-trigger:
// focus landing on a menu CONTAINER is menu-internal bookkeeping, not the user
// leaving. Focus landing on a menu ITEM still dismisses, so hovering a sibling
// submenu trigger closes this one exactly as before — which is what keeps this
// from being a "submenus never close" regression.
const isMenuContainerElement = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  target.hasAttribute("data-radix-menu-content");

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, onFocusOutside, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 [&>:first-child]:mt-0 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className,
    )}
    onFocusOutside={(event) => {
      onFocusOutside?.(event);
      if (event.defaultPrevented) return;
      if (isMenuContainerElement(event.target)) {
        event.preventDefault();
      }
    }}
    {...props}
  />
));
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
    // NOTE: Portal target override — defaults to document.body. Needed when the
    // trigger lives inside a fullscreened element: the browser only paints the
    // fullscreen subtree, so a body-portaled menu would be invisible.
    portalContainer?: Element | null;
  }
>(({ className, sideOffset = 4, portalContainer, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal container={portalContainer}>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 [&>:first-child]:mt-0 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      // NOTE: NSAI-6259 follow-up — the ONE menu-row scale. Rows across the app
      // used to set their own `py-3`, which is 24px of padding around a 20px
      // label; the row read as mostly empty space. 6px here, and the call sites
      // no longer override it, so every menu in the product has the same
      // rhythm. The text size is deliberately untouched — only the box moves.
      //
      // `min-h` rather than more padding is what keeps this usable on a touch
      // screen: a fine pointer gets the compact 32px row, a coarse one gets 44px
      // without the desktop menus growing to match. The web app is used on
      // phones at 390px (see CLAUDE.md), so this cannot be desktop-only.
      "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-start text-sm outline-none transition-colors",
      "min-h-8 pointer-coarse:min-h-11",
      "hover:bg-accent hover:text-accent-foreground",
      "focus:bg-accent focus:text-accent-foreground",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 ps-8 pe-2 text-start text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute start-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 ps-8 pe-2 text-start text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    <span className="absolute start-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="h-2 w-2 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-start text-sm font-semibold text-popover-title",
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-border", className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn("ms-auto text-xs tracking-widest opacity-60", className)}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

const DropdownMenuIcon = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) => {
  return (
    <div
      {...props}
      className={cn(
        // NOTE: NSAI-6259 follow-up — 24px, was 28px with `p-1.5`. The tile
        // used to be a filled square and was sized to be seen; now that the
        // resting fill is gone (see --dropdown-menu-icon-background) all it
        // did was hold a 16px glyph in the middle of a box taller than the
        // label beside it, which is what made every menu row feel padded.
        // It still has to be a box rather than the bare glyph, because the
        // hover fill is painted on it.
        "p-0.5 text-accent-foreground rounded-md bg-dropdown-menu-icon-background group-focus:bg-dropdown-menu-icon-background-hover h-6 w-6 flex items-center justify-center transition-colors data-[variant=destructive]:text-destructive group-focus:dark:data-[variant=destructive]:text-accent-foreground data-[variant=destructive]:bg-destructive/10 group-focus:data-[variant=destructive]:bg-destructive/15 dark:data-[variant=destructive]:bg-dropdown-menu-icon-background dark:group-focus:data-[variant=destructive]:bg-dropdown-menu-icon-background-hover",
        className,
      )}
    />
  );
};

const DropdownMenuIconItem = ({
  className,
  icon,
  iconGroupProps,
  children,
  variant = "default",
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuItem> & {
  icon: React.ReactNode;
  iconGroupProps?: React.ComponentPropsWithoutRef<typeof DropdownMenuIcon>;
  variant?: "default" | "destructive";
}) => {
  return (
    <DropdownMenuItem
      {...props}
      data-variant={variant}
      className={cn(
        "flex items-center gap-2 rounded-none group transition-colors",
        "hover:bg-accent hover:text-accent-foreground",
        "data-[variant=destructive]:text-destructive",
        "hover:data-[variant=destructive]:bg-destructive/10 hover:data-[variant=destructive]:text-destructive",
        "focus:data-[variant=destructive]:bg-destructive/10 dark:focus:text-accent-foreground focus:dark:data-[variant=destructive]:bg-accent",
        className,
      )}
    >
      <DropdownMenuIcon {...iconGroupProps} data-variant={variant}>
        {icon}
      </DropdownMenuIcon>
      {children}
    </DropdownMenuItem>
  );
};

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuIcon,
  DropdownMenuIconItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
